import React, { useState } from 'react';

export interface HistoryItem {
  request_id: string;
  timestamp: string;
  question: string;
  answer: string;
  blocked?: boolean;
  rating?: number;
}

interface HistoryViewProps {
  history: HistoryItem[];
  onSelectItem?: (item: HistoryItem) => void;
  onClearHistory?: () => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({
  history,
  onSelectItem,
  onClearHistory
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (history.length === 0) {
    return null;
  }

  const handleItemClick = (item: HistoryItem) => {
    setSelectedId(selectedId === item.request_id ? null : item.request_id);
    if (onSelectItem) {
      onSelectItem(item);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const truncateText = (text: string, maxLength: number = 50) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <svg
            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          History ({history.length})
        </button>
        {onClearHistory && history.length > 0 && (
          <button
            onClick={onClearHistory}
            className="text-xs text-gray-500 hover:text-red-600"
            title="Clear history"
          >
            Clear
          </button>
        )}
      </div>

      {/* History Items */}
      {isExpanded && (
        <div className="max-h-96 overflow-y-auto">
          {history.slice().reverse().map((item) => {
            const isSelected = selectedId === item.request_id;
            return (
              <div
                key={item.request_id}
                className={`border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleItemClick(item)}
              >
                {/* Compact view */}
                <div className="px-3 py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {item.blocked && (
                          <svg className="w-3 h-3 text-amber-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                        {item.rating && (
                          <div className="flex gap-0.5">
                            {Array.from({ length: item.rating }).map((_, i) => (
                              <svg key={i} className="w-3 h-3 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                            ))}
                          </div>
                        )}
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(item.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-900 font-medium">
                        {truncateText(item.question)}
                      </p>
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${
                        isSelected ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded view */}
                {isSelected && (
                  <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
                    <div className="text-xs space-y-2">
                      <div>
                        <span className="font-medium text-gray-700">Q: </span>
                        <span className="text-gray-600">{item.question}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">A: </span>
                        <span className="text-gray-600">
                          {truncateText(item.answer, 200)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default HistoryView;
